const request = require('@flowio/request');
const optionsSuccess = require('@flowio/request/lib/success');
const optionsError = require('@flowio/request/lib/error');
const ChannelEventEmitter = require('@flowio/events/lib/channel-event-emitter').default;
const get = require('lodash/get');
const pickBy = require('lodash/pickBy');
const isNil = require('lodash/isNil');
const inRange = require('lodash/inRange');

class FlowCart {
  constructor({ debug = false, cartId, apiBaseURI } = {}) {
    this.debug = debug;
    this.cartId = cartId;
    this.apiBaseURI = apiBaseURI;
    this.emitter = new ChannelEventEmitter('flow_cart');
    this.emitter.on('updated', ({ cart }) => {
      this.log('event:updated: setting Cart data', cart);
      this.cartData = cart;
    });
  }

  static pickByIsNotNil(key, value) {
    return !isNil(value);
  }

  log(message, ...params) {
    if (this.debug && console && console.log) { // eslint-disable-line no-console
      console.log(`[FlowCart] ${message}`, ...params); // eslint-disable-line no-console
    }
  }

  emitIfSuccessful(status, event, data) {
    if (inRange(status, 200, 300)) {
      this.emitter.trigger(event, data);
      this.emitter.trigger('message', { ...data, event });
    }
  }

  handleResponse(options, status, response, errorMessagePartial) {
    this.log('response', `status[${status}]`, response);
    if (status >= 200 && status < 300) {
      optionsSuccess(options, response);
    } else {
      const { error: { code, messages = [] } } = response;
      const codeText = code ? `code[${code}] ` : '';
      optionsError(options, `${errorMessagePartial} status[${status}] ${codeText}message[${messages[0]}]`);
    }
  }

  addItem(options = {}) {
    const { id, quantity = 1, properties } = options;
    const url = `${this.apiBaseURI}/shopify/carts/${this.cartId}/add`;
    this.log('request', `POST ${url}`, { id, quantity, properties });
    request.authenticated({
      url,
      method: 'POST',
      data: pickBy({
        discriminator: 'shopify_cart_add_single_form',
        id,
        quantity,
        properties,
      }, FlowCart.pickByIsNotNil),
    }, (status, response) => {
      this.emitIfSuccessful(status, 'addItem', { id, quantity, properties, cart: response });
      this.emitIfSuccessful(status, 'updated', { cart: response });
      this.handleResponse(options, status, response, `Failed to add item. id[${id}] quantity[${quantity}] properties[${JSON.stringify(properties)}]`);
    });
  }

  addItems(options = {}) {
    const { items = [] } = options;
    const url = `${this.apiBaseURI}/shopify/carts/${this.cartId}/add`;
    this.log('request', `POST ${url}`, options);

    request.authenticated({
      url,
      method: 'POST',
      data: {
        discriminator: 'shopify_cart_add_multiple_form',
        items,
      },
    }, (status, response) => {
      this.emitIfSuccessful(status, 'addItems', { items, cart: response });
      this.emitIfSuccessful(status, 'updated', { cart: response });
      this.handleResponse(options, status, response, `Failed to add items. items[${JSON.stringify(items)}]`);
    });
  }

  updateItem(options = {}) {
    const { id, quantity = 1, properties } = options;
    const url = `${this.apiBaseURI}/shopify/carts/${this.cartId}/change`;
    this.log('request', `POST ${url}`, { id, quantity, properties });
    request.authenticated({
      url,
      method: 'POST',
      data: pickBy({
        id,
        quantity,
        properties,
      }, FlowCart.pickByIsNotNil),
    }, (status, response) => {
      this.emitIfSuccessful(status, 'updateItem', { id, quantity, properties, removeItem: quantity === 0, cart: response });
      this.emitIfSuccessful(status, 'updated', { cart: response });
      const propertiesText = properties ? ` properties[${JSON.stringify(properties)}]` : '';
      this.handleResponse(options, status, response, `Failed to update item. id[${id}] quantity[${quantity}]${propertiesText}`);
    });
  }

  getCart(options = {}) {
    const url = `${this.apiBaseURI}/shopify/carts/${this.cartId}`;
    this.log('request', `GET ${url}`);

    request.authenticated({
      url,
    }, (status, response) => {
      this.emitIfSuccessful(status, 'getCart', { cart: response });
      this.emitIfSuccessful(status, 'updated', { cart: response });
      this.handleResponse(options, status, response, 'Failed to get cart.');
    });
  }

  removeItem(options = {}) {
    this.updateItem({
      ...options,
      quantity: 0,
      onSuccess: (cart) => {
        this.emitIfSuccessful(status, 'removeItem', { cart });
        optionsSuccess(options, cart);
      },
    });
  }

  // TODO: Properly cache cart and merge existing attributes with ones added below. Aka copy what
  //       is happening in ShopifyCart
  setAttributes(options = {}) {
    const attributes = pickBy(get(options, 'attributes'));
    const url = `${this.apiBaseURI}/shopify/carts/${this.cartId}/attributes`;
    this.log('request', `GET ${url}`);

    request.authenticated({
      url,
      method: 'PUT',
      data: { attributes },
    }, (status, response) => {
      this.emitIfSuccessful(status, 'setAttributes', { attributes, cart: response });
      this.emitIfSuccessful(status, 'updated', { cart: response });
      this.handleResponse(options, status, response, 'Failed to update cart attributes.');
    });
  }

  clear(options = {}) {
    const url = `${this.apiBaseURI}/shopify/carts/${this.cartId}/clear`;
    this.log('request', `POST ${url}`);

    request.authenticated({
      url,
      method: 'POST',
    }, (status, response) => {
      this.emitIfSuccessful(status, 'clear', { cart: response });
      this.emitIfSuccessful(status, 'updated', { cart: response });
      this.handleResponse(options, status, response, 'Failed to clear cart.');
    });
  }
}

module.exports = FlowCart;



// WEBPACK FOOTER //
// ./static/js/v0/cart_unified/FlowCart.js